import {
  forwardRef,
  useId,
  useRef,
  type ChangeEvent,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { Icon, type IconName } from '@serota/icons/web';
import { formatPlate } from '@serota/i18n';
import { cx } from '../lib';

export interface FieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  htmlFor?: string;
  className?: string;
  children: ReactNode;
}

/** Etiket her zaman görünür; placeholder etiket değildir. Hata alanın altında, ikonla ve sonraki adımla. */
export function Field({ label, hint, error, htmlFor, className, children }: FieldProps) {
  return (
    <div className={cx('sr-field', className)}>
      {label ? (
        <label className="sr-field__label" htmlFor={htmlFor}>
          {label}
        </label>
      ) : null}
      {children}
      {error ? (
        <span className="sr-field__error" role="alert">
          <Icon name="uyari" />
          {error}
        </span>
      ) : hint ? (
        <span className="sr-field__hint">{hint}</span>
      ) : null}
    </div>
  );
}

export interface TextInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  size?: 'md' | 'sm';
  fieldClassName?: string;
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  { label, hint, error, size = 'md', id, className, fieldClassName, ...rest },
  ref,
) {
  const auto = useId();
  const inputId = id ?? auto;
  const input = (
    <input
      ref={ref}
      id={inputId}
      className={cx('sr-input', size === 'sm' && 'sr-input--sm', className)}
      aria-invalid={error ? true : undefined}
      {...rest}
    />
  );
  if (!label && !hint && !error) return input;
  return (
    <Field label={label} hint={hint} error={error} htmlFor={inputId} className={fieldClassName}>
      {input}
    </Field>
  );
});

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  { label, hint, error, id, className, ...rest },
  ref,
) {
  const auto = useId();
  const inputId = id ?? auto;
  return (
    <Field label={label} hint={hint} error={error} htmlFor={inputId}>
      <textarea
        ref={ref}
        id={inputId}
        className={cx('sr-input', className)}
        aria-invalid={error ? true : undefined}
        {...rest}
      />
    </Field>
  );
});

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  options: SelectOption[];
  placeholder?: string;
  size?: 'md' | 'sm';
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, hint, error, options, placeholder, size = 'md', id, className, ...rest },
  ref,
) {
  const auto = useId();
  const inputId = id ?? auto;
  const control = (
    <div className="sr-select-wrap">
      <select
        ref={ref}
        id={inputId}
        className={cx('sr-input', 'sr-select', size === 'sm' && 'sr-input--sm', className)}
        aria-invalid={error ? true : undefined}
        {...rest}
      >
        {placeholder ? (
          <option value="" disabled>
            {placeholder}
          </option>
        ) : null}
        {options.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
      </select>
      <Icon name="chev-down" />
    </div>
  );
  if (!label && !hint && !error) return control;
  return (
    <Field label={label} hint={hint} error={error} htmlFor={inputId}>
      {control}
    </Field>
  );
});

export interface PhoneInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  value: string;
  onChange: (digits: string) => void;
}

/** +90 önekli telefon; değer 10 haneli yerel numara (5xx…). */
export function PhoneInput({ label = 'Telefon numarası', hint, error, value, onChange, id, ...rest }: PhoneInputProps) {
  const auto = useId();
  const inputId = id ?? auto;
  const pretty = value
    .replace(/\D/g, '')
    .slice(0, 10)
    .replace(/^(\d{3})(\d{0,3})(\d{0,2})(\d{0,2}).*/, (_m, a, b, c, d) => [a, b, c, d].filter(Boolean).join(' '));
  return (
    <Field label={label} hint={hint} error={error} htmlFor={inputId}>
      <div className={cx('sr-input', error && 'is-error')}>
        <span className="sr-input__prefix">+90</span>
        <input
          id={inputId}
          className="sr-input__control"
          inputMode="tel"
          autoComplete="tel-national"
          placeholder="5xx xxx xx xx"
          value={pretty}
          aria-invalid={error ? true : undefined}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            onChange(e.target.value.replace(/\D/g, '').replace(/^0/, '').slice(0, 10))
          }
          {...rest}
        />
      </div>
    </Field>
  );
}

export interface OtpInputProps {
  value: string;
  onChange: (code: string) => void;
  length?: number;
  error?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
  label?: string;
}

/** 6 kutulu OTP: otomatik odak, yapıştırma ve SMS otomatik doldurma (autocomplete=one-time-code). */
export function OtpInput({
  value,
  onChange,
  length = 6,
  error,
  autoFocus,
  disabled,
  label = 'Doğrulama kodu',
}: OtpInputProps) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const digits = Array.from({ length }, (_, i) => value[i] ?? '');
  const setAt = (i: number, d: string) => {
    const next = (value.slice(0, i) + d + value.slice(i + 1)).replace(/\D/g, '').slice(0, length);
    onChange(next);
  };
  const onInput = (i: number, e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '');
    if (raw.length > 1) {
      onChange(raw.slice(0, length));
      refs.current[Math.min(raw.length, length) - 1]?.focus();
      return;
    }
    setAt(i, raw);
    if (raw && i < length - 1) refs.current[i + 1]?.focus();
  };
  const onKey = (i: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) refs.current[i - 1]?.focus();
    if (e.key === 'ArrowLeft' && i > 0) refs.current[i - 1]?.focus();
    if (e.key === 'ArrowRight' && i < length - 1) refs.current[i + 1]?.focus();
  };
  return (
    <div className={cx('sr-otp', error && 'is-error')} role="group" aria-label={label}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          value={d}
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          maxLength={length}
          autoFocus={autoFocus && i === 0}
          disabled={disabled}
          aria-label={`${label} ${i + 1}. hane`}
          aria-invalid={error || undefined}
          onChange={(e) => onInput(i, e)}
          onKeyDown={(e) => onKey(i, e)}
          onFocus={(e) => e.target.select()}
        />
      ))}
    </div>
  );
}

export interface PlateInputProps {
  value: string;
  onChange: (plate: string) => void;
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  autoFocus?: boolean;
}

/** TR plaka: büyük harfe çevrilir ve "34 ABC 123" biçiminde boşluklanır. */
export function PlateInput({
  value,
  onChange,
  label = 'Plaka',
  hint = 'Biçim: 34 ABC 123',
  error,
  autoFocus,
}: PlateInputProps) {
  const id = useId();
  return (
    <Field label={label} hint={hint} error={error} htmlFor={id}>
      <div className="sr-plate">
        <span className="sr-plate__tr">TR</span>
        <input
          id={id}
          value={value}
          autoFocus={autoFocus}
          autoCapitalize="characters"
          spellCheck={false}
          placeholder="34 ABC 123"
          onChange={(e) => onChange(formatPlate(e.target.value))}
        />
      </div>
    </Field>
  );
}

export interface SearchInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  width?: number | string;
}

export function SearchInput({ width, className, placeholder = 'Ara', ...rest }: SearchInputProps) {
  return (
    <div className={cx('sr-search', className)} style={{ width }}>
      <Icon name="search" />
      <input className="sr-input" type="search" placeholder={placeholder} aria-label={placeholder} {...rest} />
    </div>
  );
}

export interface ToggleProps {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  disabled?: boolean;
  id?: string;
}

export function Toggle({ checked, onChange, label, disabled, id }: ToggleProps) {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      className={cx('sr-toggle', checked && 'is-on')}
      onClick={() => onChange(!checked)}
    />
  );
}

/** Ayar satırı: etiket (+ açıklama) solda, anahtar sağda. Etiket tıklanabilir alanın parçası. */
export function SwitchRow({
  label,
  description,
  checked,
  onChange,
  disabled,
}: {
  label: ReactNode;
  description?: ReactNode;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <div className="sr-switch-row">
      <label htmlFor={id} style={{ flex: 1, minWidth: 0, cursor: disabled ? 'default' : 'pointer' }}>
        <div style={{ fontWeight: 600, fontSize: 15 }}>{label}</div>
        {description ? <div className="caption sr-muted">{description}</div> : null}
      </label>
      <Toggle
        id={id}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        label={typeof label === 'string' ? label : 'Aç / kapat'}
      />
    </div>
  );
}

export interface CheckboxProps {
  checked: boolean;
  onChange: (v: boolean) => void;
  label?: ReactNode;
  ariaLabel?: string;
  indeterminate?: boolean;
  small?: boolean;
}

export function Checkbox({ checked, onChange, label, ariaLabel, indeterminate, small }: CheckboxProps) {
  const box = (
    <button
      type="button"
      role="checkbox"
      aria-checked={indeterminate ? 'mixed' : checked}
      aria-label={ariaLabel ?? (typeof label === 'string' ? label : undefined)}
      className={cx('sr-check', (checked || indeterminate) && 'is-on')}
      style={small ? { width: 18, height: 18 } : undefined}
      onClick={() => onChange(!checked)}
    >
      {indeterminate ? (
        <Icon name="plus" style={{ transform: 'rotate(0deg)' }} />
      ) : checked ? (
        <Icon name="check" />
      ) : null}
    </button>
  );
  if (!label) return box;
  return (
    <label className="sr-row" style={{ gap: 12, cursor: 'pointer' }}>
      {box}
      <span>{label}</span>
    </label>
  );
}

export interface OptionCardProps {
  selected: boolean;
  onSelect: () => void;
  title: ReactNode;
  description?: ReactNode;
  icon?: IconName;
  trailing?: ReactNode;
  disabled?: boolean;
}

/** Seçenek kartı + radyo (sheet ve formlarda). */
export function OptionCard({ selected, onSelect, title, description, icon, trailing, disabled }: OptionCardProps) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      disabled={disabled}
      className={cx('sr-option', selected && 'is-selected')}
      onClick={onSelect}
    >
      <span className="sr-radio" />
      {icon ? <Icon name={icon} /> : null}
      <span style={{ flex: 1 }}>
        <b>{title}</b>
        {description ? (
          <span className="caption sr-muted" style={{ display: 'block' }}>
            {description}
          </span>
        ) : null}
      </span>
      {trailing}
    </button>
  );
}
